// src/components/reviews/CompanyValuesTableEmployee.jsx
import React, { useMemo, useState } from 'react';
import './reviews-table.css';

/**
 * Props:
 * - rows: Array<{
 *     company_value: { id, name, weight },
 *     review?: {
 *       id,
 *       employee_rating_id, employee_description,
 *       manager_rating_id,  manager_description
 *     }
 *   }>
 * - ratings: Array<{ id, code, name, score? }>
 * - reviewTypeId: number
 * - onSaveRow: ({ mode: 'create'|'update', reviewId?, payload }) => Promise<void>
 *   payload will include: { company_value_id, review_type_id, employee_rating_id, employee_description }
 * - readOnly?: boolean (default: false) — if you ever want to lock the table
 */
export default function CompanyValuesTableEmployee({
                                                       rows = [],
                                                       ratings = [],
                                                       reviewTypeId,
                                                       onSaveRow,
                                                       readOnly = false,
                                                   }) {
    // index ratings by (score || id) for quick lookups
    const ratingIndex = useMemo(() => {
        const map = new Map();
        ratings.forEach(r => {
            const key = r?.score ?? r?.id;
            map.set(Number(key), r);
        });
        return map;
    }, [ratings]);

    // local draft state per row (keyed by reviewId || company_value_id)
    const [drafts, setDrafts] = useState({}); // { [key]: { employee_rating_id, employee_description } }
    const rowKey = (row) => row?.review?.id ?? `cv-${row?.company_value?.id}`;

    const getDraft = (row) => {
        const key = rowKey(row);
        const base = row.review || {};
        return drafts[key] ?? {
            employee_rating_id: base.employee_rating_id ?? '',
            employee_description: base.employee_description ?? '',
        };
    };

    const setDraft = (row, patch) => {
        const key = rowKey(row);
        setDrafts(prev => ({ ...prev, [key]: { ...getDraft(row), ...patch } }));
    };

    const onChangeEmployeeRating = (row, value) => {
        const num = value === '' ? '' : Number(value);
        const picked = ratingIndex.get(Number(num));
        setDraft(row, {
            employee_rating_id: num,
            employee_description: picked?.code ?? '',
        });
    };

    const onSave = async (row) => {
        const key = rowKey(row);
        const draft = drafts[key] ?? {};
        const isUpdate = Boolean(row?.review?.id);

        const payload = {
            company_value_id: row?.company_value?.id,
            review_type_id: reviewTypeId,
            employee_rating_id: draft.employee_rating_id,
            employee_description: draft.employee_description,
        };

        await onSaveRow({
            mode: isUpdate ? 'update' : 'create',
            reviewId: row?.review?.id,
            payload,
        });
    };

    // helpers for display-only cells
    const fmtManagerRating = (row) => {
        const v = row?.review?.manager_rating_id;
        if (v == null) return '—';
        return String(v);
    };
    const fmtManagerDesc = (row) => row?.review?.manager_description ?? '—';

    return (
        <div className="card mt-5">
            <div className="card-header">
                <h6 className="mb-0">Company Values</h6>
            </div>
            <div className="card-body">
                <div className="reviews-table mt-3">
                    <table>
                        <thead>
                        <tr className="rt-head-top">
                            <th className="rt-counter">#</th>
                            <th className="rt-name">Company Value</th>
                            <th className="rt-num" style={{ width: 100 }}>Weight</th>

                            <th className="rt-num" style={{ width: 130 }}>Employee Rating</th>
                            <th className="rt-num" style={{ width: 150 }}>Employee Description</th>

                            <th className="rt-num" style={{ width: 130 }}>Manager Rating</th>
                            <th className="rt-num" style={{ width: 150 }}>Manager Description</th>

                            <th className="rt-actions" style={{ width: 100 }}>Action</th>
                        </tr>
                        </thead>

                        <tbody>
                        {rows.length === 0 && (
                            <tr><td colSpan={8} className="text-center text-muted py-3">No company values found.</td></tr>
                        )}

                        {rows.map((row, idx) => {
                            const counter = String(idx + 1).padStart(2, '0');
                            const draft = getDraft(row);

                            return (
                                <tr key={row?.company_value?.id ?? idx}>
                                    <td className="rt-counter">CV {counter}</td>
                                    <td className="rt-name">{row?.company_value?.name ?? '—'}</td>
                                    <td className="rt-num">{Number(row?.company_value?.weight ?? 0).toFixed(2)}</td>

                                    {/* Employee Rating (editable) */}
                                    <td className="rt-num">
                                        <select
                                            className="rt-input rt-select"
                                            value={draft.employee_rating_id}
                                            onChange={(e) => onChangeEmployeeRating(row, e.target.value)}
                                            disabled={readOnly}
                                        >
                                            <option value="">—</option>
                                            {ratings.map(r => {
                                                const val = r?.score ?? r?.id;
                                                return (
                                                    <option key={String(val)} value={String(val)}>
                                                        {String(val)}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                    </td>

                                    {/* Employee Description (auto from code, read-only) */}
                                    <td className="rt-num">
                                        <input
                                            className="rt-input"
                                            value={draft.employee_description || ''}
                                            readOnly
                                        />
                                    </td>

                                    {/* Manager Rating (read-only) */}
                                    <td className="rt-num">
                                        <input
                                            className="rt-input"
                                            value={fmtManagerRating(row)}
                                            readOnly
                                        />
                                    </td>

                                    {/* Manager Description (read-only) */}
                                    <td className="rt-num">
                                        <input
                                            className="rt-input"
                                            value={fmtManagerDesc(row)}
                                            readOnly
                                        />
                                    </td>

                                    {/* Action */}
                                    <td className="rt-actions">
                                        <button
                                            className="rt-btn rt-btn-primary"
                                            onClick={() => onSave(row)}
                                            disabled={readOnly}
                                        >
                                            Save
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                        </tbody>

                    </table>
                </div>
            </div>
        </div>

    );
}